// ViewSupport49Component.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Label, Input, Table } from "reactstrap";
import { Loading } from "../../share/loading";
import "../../share/form.css";
import { Formik, Field, Form, FieldArray } from "formik";
import Swal from "sweetalert2";
import Select from "react-select";
import { GetVMdDept } from "../../share/api/MasterDataApi";
import { NumberWithCommas } from "../../share/NumberWithCommas";

import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* ===== locales ===== */
registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

/* ===== constants & helpers ===== */
const TH_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const YEARS = range(1963, new Date().getFullYear() + 8);

const N = (v) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
const fmtMoney = (n) =>
  Number(String(n ?? 0).replace(/,/g, "")).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** parse วันที่ไทย loosely เช่น "1 มกราคม 2567" → Date */
const parseThaiDateLoose = (s) => {
  if (!s || typeof s !== "string") return null;
  const parts = s.trim().split(/\s+/);
  if (parts.length < 3) return null;

  const [dStr, mStr, yStr] = parts;

  const d = parseInt(dStr.replace(/\D/g, ""), 10);
  const monthIdx = TH_MONTHS.findIndex((m) => m === mStr);
  const be = parseInt(yStr.replace(/\D/g, ""), 10);

  if (isNaN(d) || monthIdx < 0 || isNaN(be)) return null;

  const ad = be - 543;
  const dt = new Date(ad, monthIdx, d);

  return isNaN(dt) ? null : dt;
};

const formatMonthYearBE = (date) => {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
};

/* ===== PDF Thai font loader ===== */
function base64FromArrayBuffer(buf) {
  const b = new Uint8Array(buf);
  const c = 0x8000;
  let s = "";
  for (let i = 0; i < b.length; i += c) {
    s += String.fromCharCode.apply(null, b.subarray(i, i + c));
  }
  return btoa(s);
}

let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;

  const [regRes, boldRes] = await Promise.all([
    fetch("/fonts/Sarabun-Regular.ttf"),
    fetch("/fonts/Sarabun-Bold.ttf"),
  ]);

  const [regBuf, boldBuf] = await Promise.all([
    regRes.arrayBuffer(),
    boldRes.arrayBuffer(),
  ]);

  const regB64 = base64FromArrayBuffer(regBuf);
  const boldB64 = base64FromArrayBuffer(boldBuf);

  jsPDF.API.events.push([
    "addFonts",
    function () {
      this.addFileToVFS("Sarabun-Regular.ttf", regB64);
      this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
      this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
      this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
    },
  ]);

  new jsPDF();
  thaiFontLoaded = true;
}

/* ===== Component ===== */
export function OperationReport48Component() {
  const user = JSON.parse(localStorage.getItem("userData") || "{}");

  const [loading, setLoading] = useState(false);
  const [deptsData, setDeptsData] = useState([]);
  const [reportUsingMoney, setReportUsingMoney] = useState([]);

  // Filters
  const [deptIdFilter, setDeptIdFilter] = useState("all");
  const [reportMonth, setReportMonth] = useState(""); // YYYY-MM
  const [reportMonthDate, setReportMonthDate] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 👉 เพิ่มตรงนี้ (โชว์คอลัมน์หน่วยงานเฉพาะตอนเลือกทุกหน่วยงาน)
  const showDeptColumn = deptIdFilter === "all";

  useEffect(() => {
    GetVMdDept({ setDeptsData, setLoading });
    GetReportUsingMoney();
  }, []);

  const GetReportUsingMoney = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/Support/reportUsingMoney", {
        method: "POST",
        headers: {
          token: user.token,
          "Content-type": "application/json",
        },
      });
      const resJson = await res.json();

      const filterWaitApprove = (resJson?.dataenum || []).filter((item) =>
        String(item?.supportStatusName || "").endsWith("4-8")
      );

      setReportUsingMoney(filterWaitApprove);
      setPage(1);
    } catch (e) {
      Swal.fire("ดึงข้อมูลไม่สำเร็จ", "", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!reportMonth) {
      setReportMonthDate(null);
      return;
    }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  /* ===== Apply Filters ===== */
  const filtered = useMemo(() => {
    let data = reportUsingMoney;

    if (deptIdFilter !== "all") {
      data = data.filter((it) => String(it.deptId) === String(deptIdFilter));
    }

    if (reportMonth) {
      const [y, m] = reportMonth.split("-").map(Number);
      data = data.filter((it) => {
        const dt = parseThaiDateLoose(it.paymentDate);
        if (!dt) return false;
        return dt.getFullYear() === y && dt.getMonth() + 1 === m;
      });
    }

    return data;
  }, [reportUsingMoney, deptIdFilter, reportMonth]);

  /* ===== Pagination ===== */
  const totalRows = filtered.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));

  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  /* ========================= Export: Excel ========================= */
  const handleExportExcel = () => {
    if (!filtered.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }

    // Header (Dynamic)
    const aoa = [];

    const headRow1 = ["ลำดับ"];
    const headRow2 = [""];

    if (showDeptColumn) {
      headRow1.push("หน่วยงาน");
      headRow2.push("");
    }

    headRow1.push(
      "สาขาอาชีพ",
      "สาขา",
      "จำนวนผู้เข้ารับการทดสอบ",
      "จำนวนผู้ผ่านการทดสอบ",
      "จำนวนเงินช่วยเหลือหรืออุดหนุน",
      "",
      "",
      "",
      "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน"
    );

    headRow2.push("", "", "", "",
      "ค่าตอบแทน",
      "ค่าเดินทาง",
      "ค่าวัสดุ",
      "รวม",
      ""
    );

    aoa.push(headRow1);
    aoa.push(headRow2);

    const merges = [];
    let shift = showDeptColumn ? 1 : 0;

    merges.push({ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } });
    if (showDeptColumn) merges.push({ s: { r: 0, c: 1 }, e: { r: 1, c: 1 } });
    merges.push({ s: { r: 0, c: 1 + shift }, e: { r: 1, c: 1 + shift } });
    merges.push({ s: { r: 0, c: 2 + shift }, e: { r: 1, c: 2 + shift } });
    merges.push({ s: { r: 0, c: 3 + shift }, e: { r: 1, c: 3 + shift } });
    merges.push({ s: { r: 0, c: 4 + shift }, e: { r: 1, c: 4 + shift } });
    merges.push({ s: { r: 0, c: 5 + shift }, e: { r: 0, c: 8 + shift } });
    merges.push({ s: { r: 0, c: 9 + shift }, e: { r: 1, c: 9 + shift } });

    filtered.forEach((item, idx) => {
      const sumAmt =
        N(item.compensationExpense) +
        N(item.travellingExpense) +
        N(item.materialExpense);

      const row = [idx + 1];

      if (showDeptColumn) row.push(item.deptName || "-");

      row.push(
        item.lawOccupationName || "-",
        item.testOccupationName || "-",
        N(item.personTest),
        N(item.passTest),
        N(item.compensationExpense),
        N(item.travellingExpense),
        N(item.materialExpense),
        sumAmt,
        item.paymentDate || "-"
      );

      aoa.push(row);
    });
    // ===== SUMMARY =====
    const totalComp = filtered.reduce((s, x) => s + N(x.compensationExpense), 0);
    const totalTravel = filtered.reduce((s, x) => s + N(x.travellingExpense), 0);
    const totalMaterial = filtered.reduce((s, x) => s + N(x.materialExpense), 0);
    const totalSum = totalComp + totalTravel + totalMaterial;
    const totalPerson = filtered.reduce((s, x) => s + N(x.personTest), 0);
    const totalPass = filtered.reduce((s, x) => s + N(x.passTest), 0);

    // summary row dynamic
    if (showDeptColumn) {
      aoa.push([
        "รวม",
        "",
        "",
        "",
        totalPerson,
        totalPass,
        totalComp,
        totalTravel,
        totalMaterial,
        totalSum,
        ""
      ]);
    } else {
      aoa.push([
        "รวม",
        "",
        "",
        totalPerson,
        totalPass,
        totalComp,
        totalTravel,
        totalMaterial,
        totalSum,
        ""
      ]);
    }

    const summaryRow = aoa.length - 1;

    // merge "รวม" col 0 → col (dept?1:1)
    const endMergeCol = showDeptColumn ? 1 : 0;
    merges.push({
      s: { r: summaryRow, c: 0 },
      e: { r: summaryRow, c: endMergeCol }
    });


    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;

    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(wb, "รายงานชอ48.xlsx");
  };

  /* ========================= Export: PDF ========================= */
  const handleExportPDF = async () => {
    if (!filtered.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }
    await ensureThaiFontLoaded();

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a3",
    });

    doc.setFont("Sarabun", "bold");
    doc.setFontSize(18);
    doc.text("แบบรายงาน ชอ.4-8", 40, 40);

    const head = [
      [
        { content: "ลำดับ", rowSpan: 2 },
        ...(showDeptColumn ? [{ content: "หน่วยงาน", rowSpan: 2 }] : []),
        { content: "สาขาอาชีพ", rowSpan: 2 },
        { content: "สาขา", rowSpan: 2 },
        { content: "ผู้เข้าทดสอบ", rowSpan: 2 },
        { content: "ผู้ผ่าน", rowSpan: 2 },
        { content: "จำนวนเงินช่วยเหลือหรืออุดหนุน", colSpan: 4 },
        { content: "วันที่จ่าย", rowSpan: 2 },
      ],
      [
        { content: "ค่าตอบแทน" },
        { content: "ค่าเดินทาง" },
        { content: "ค่าวัสดุ" },
        { content: "รวม" },
      ],
    ];

    const body = filtered.map((item, idx) => {
      const sumAmt =
        N(item.compensationExpense) +
        N(item.travellingExpense) +
        N(item.materialExpense);

      const row = [idx + 1];

      if (showDeptColumn) row.push(item.deptName || "-");

      row.push(
        item.lawOccupationName || "-",
        item.testOccupationName || "-",
        fmtMoney(N(item.personTest)),
        fmtMoney(N(item.passTest)),
        fmtMoney(N(item.compensationExpense)),
        fmtMoney(N(item.travellingExpense)),
        fmtMoney(N(item.materialExpense)),
        fmtMoney(sumAmt),
        item.paymentDate || "-"
      );
      return row;
    });

    doc.setFont("Sarabun", "normal");
    doc.autoTable({
      head,
      body,
      startY: 60,
      styles: { font: "Sarabun", fontSize: 10, cellPadding: 4 },
      headStyles: {
        fillColor: [240, 240, 240],
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {},
    });
    const totalComp = filtered.reduce((s, x) => s + N(x.compensationExpense), 0);
    const totalTravel = filtered.reduce((s, x) => s + N(x.travellingExpense), 0);
    const totalMaterial = filtered.reduce((s, x) => s + N(x.materialExpense), 0);
    const totalSum = totalComp + totalTravel + totalMaterial;
    const totalPerson = filtered.reduce((s, x) => s + N(x.personTest), 0);
    const totalPass = filtered.reduce((s, x) => s + N(x.passTest), 0);

    const y = doc.lastAutoTable.finalY + 25;

    doc.setFont("Sarabun", "bold");
    doc.setFontSize(12);

    doc.text(
      `รวม: ผู้เข้าทดสอบ ${fmtMoney(totalPerson)} คน | ผ่าน ${fmtMoney(totalPass)} คน | ` +
      `ค่าตอบแทน ${fmtMoney(totalComp)} | เดินทาง ${fmtMoney(totalTravel)} | วัสดุ ${fmtMoney(totalMaterial)} | รวม ${fmtMoney(totalSum)} บาท`,
      40,
      y
    );

    doc.save("รายงานชอ48.pdf");
  };
  return (
    <Formik initialValues={{}} onSubmit={() => { }}>
      {(formik) => (
        <Container className="py-3">
          <Form>

            {/* Filters */}
            <Row className="g-3 align-items-end">
              <Col md={6}>
                <Label>ชื่อหน่วยงาน</Label>
                <Select
                  isSearchable
                  options={[{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])]}
                  value={[{ value: "all", label: "ทุกหน่วยงาน" }, ...(deptsData || [])].find(
                    (o) => String(o.value) === String(deptIdFilter)
                  )}
                  onChange={(opt) => {
                    setDeptIdFilter(opt?.value ?? "all");
                    setPage(1);
                  }}
                  placeholder={<div className="text-center">กรุณาเลือกหน่วยงาน</div>}
                />
              </Col>

              <Col md={6}>
                <Label>เดือน/ปี ที่จ่ายเงิน</Label>
                <DatePicker
                  selected={reportMonthDate}
                  onChange={(date) => {
                    if (date instanceof Date && !isNaN(date)) {
                      const y = date.getFullYear();
                      const m = String(date.getMonth() + 1).padStart(2, "0");
                      setReportMonth(`${y}-${m}`);
                      setReportMonthDate(date);
                    } else {
                      setReportMonth("");
                      setReportMonthDate(null);
                    }
                    setPage(1);
                  }}
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                  locale="th-buddhist"
                  customInput={
                    <input
                      className="form-control text-center"
                      readOnly
                      value={formatMonthYearBE(reportMonthDate)}
                      placeholder="เลือกเดือน/ปี (พ.ศ.)"
                    />
                  }
                />
              </Col>

              <Col md="12" className="pt-2 d-flex gap-2">
                <button type="button" className="btn btn-outline-success" onClick={handleExportExcel}>
                  ส่งออก Excel
                </button>
                <button type="button" className="btn btn-outline-danger" onClick={handleExportPDF}>
                  ส่งออก PDF
                </button>
              </Col>
            </Row>

            {/* Table */}
            <div className="mt-3">
              <Table bordered responsive>
                <thead className="table-light">
                  <tr>
                    <th rowSpan={2}>ลำดับ</th>

                    {showDeptColumn && <th rowSpan={2}>หน่วยงาน</th>}

                    <th rowSpan={2}>สาขาอาชีพ</th>
                    <th rowSpan={2}>สาขา</th>
                    <th rowSpan={2}>จำนวนผู้เข้ารับการทดสอบ</th>
                    <th rowSpan={2}>จำนวนผู้ผ่านการทดสอบ</th>
                    <th colSpan={4} className="text-center">จำนวนเงินช่วยเหลือหรืออุดหนุน</th>
                    <th rowSpan={2}>วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
                  </tr>
                  <tr>
                    <th>ค่าตอบแทน</th>
                    <th>ค่าเดินทาง</th>
                    <th>ค่าวัสดุ</th>
                    <th>รวม</th>
                  </tr>
                </thead>

                {!loading ? (
                  <tbody>
                    {pageData.length > 0 ? (
                      pageData.map((item, i) => {
                        const idx = (page - 1) * pageSize + i + 1;

                        const sumAmt =
                          N(item.compensationExpense) +
                          N(item.travellingExpense) +
                          N(item.materialExpense);

                        return (
                          <tr key={idx}>
                            <td className="text-center">{idx}</td>

                            {showDeptColumn && (
                              <td className="text-center">{item.deptName || "-"}</td>
                            )}

                            <td className="text-center">{item.lawOccupationName || "-"}</td>
                            <td className="text-center">{item.testOccupationName || "-"}</td>
                            <td className="text-center">{NumberWithCommas(item.personTest ?? 0)}</td>
                            <td className="text-center">{NumberWithCommas(item.passTest ?? 0)}</td>

                            <td className="text-center">
                              {NumberWithCommas(item.compensationExpense ?? 0)}
                            </td>
                            <td className="text-center">
                              {NumberWithCommas(item.travellingExpense ?? 0)}
                            </td>
                            <td className="text-center">
                              {NumberWithCommas(item.materialExpense ?? 0)}
                            </td>

                            <td className="text-center">
                              {NumberWithCommas(sumAmt)}
                            </td>

                            <td className="text-center">{item.paymentDate || "-"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={showDeptColumn ? 11 : 10}
                          className="text-center"
                        >
                          ไม่มีข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={showDeptColumn ? 11 : 10}>
                        <Loading />
                      </td>
                    </tr>
                  </tbody>
                )}
                <tfoot className="table-light">
                  <tr>
                    <th className="text-center" colSpan={showDeptColumn ? 2 : 1}>รวม</th>

                    <th className="text-center"></th>
                    <th className="text-center"></th>

                    <th className="text-center">
                      {NumberWithCommas(filtered.reduce((s, x) => s + N(x.personTest), 0))}
                    </th>

                    <th className="text-center">
                      {NumberWithCommas(filtered.reduce((s, x) => s + N(x.passTest), 0))}
                    </th>

                    <th className="text-center">
                      {NumberWithCommas(filtered.reduce((s, x) => s + N(x.compensationExpense), 0))}
                    </th>

                    <th className="text-center">
                      {NumberWithCommas(filtered.reduce((s, x) => s + N(x.travellingExpense), 0))}
                    </th>

                    <th className="text-center">
                      {NumberWithCommas(filtered.reduce((s, x) => s + N(x.materialExpense), 0))}
                    </th>

                    <th className="text-center">
                      {NumberWithCommas(
                        filtered.reduce(
                          (s, x) => s +
                            N(x.compensationExpense) +
                            N(x.travellingExpense) +
                            N(x.materialExpense),
                          0
                        )
                      )}
                    </th>

                    <th className="text-center">-</th>
                  </tr>
                </tfoot>

              </Table>
            </div>

            {/* Pagination */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
              <div className="d-flex align-items-center gap-2">
                <span>แสดงต่อหน้า:</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 100 }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 20, 50, 100].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <span className="ms-2">ทั้งหมด {totalRows.toLocaleString()} รายการ</span>
              </div>

              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage(1)}
                >
                  {'«'}
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {'‹'}
                </button>
                <span>
                  หน้า {page} / {pageCount}
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  {'›'}
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page >= pageCount}
                  onClick={() => setPage(pageCount)}
                >
                  {'»'}
                </button>
              </div>
            </div>
          </Form>
        </Container>
      )}
    </Formik>
  );
}

